// The compendium's parts (issue 1585, slice 1): one entry's row — its name, its family as a small badge, its book in
// muted text, a right-hand detail (owner, how many, folder) and the DM's actions where they apply — shared by the
// default view's groups and the search's results; and one group of the default view — a gold Cinzel header that opens
// and closes it, remembered for this person and this group (default open), up to five rows with "+ N more" for the
// rest, and nothing at all when it is empty unless it says so in words (`emptyText`). Slice 2: a row may carry a
// "hidden" tag (the DM's tokens on the scene), and a `TextRow` is one with no entry to open (a condition the site has
// no page for, an entry a player was sent by its name alone).
//
// Slice 3: a row may raise its entry's hover card (`preview`): after the pointer rests on it `INTENT_MS` (the site's
// hover intent), at once when it is focused from the keyboard, or after a press-and-hold of `HOLD_MS` on a touch screen
// (that press's click then does nothing; a plain tap opens the page as before). The card is drawn inside the row, so
// Tab reaches it and the pointer may cross onto it; leaving both (after a short grace), focus leaving both, or Escape
// closes it — Escape keeps focus on the row.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** How long the pointer rests on a row before its card is raised (the site's `INTENT_MS`), and a touch is held. */
export const INTENT_MS = 150;
export const HOLD_MS = 500;
/** How long the card waits, after the pointer leaves the row and the card, before it closes. */
export const LEAVE_MS = 120;

/** A row's hover card: whether it is up, how to raise or close it, and the card to draw beside the row. */
export interface RowPreview {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: (anchor: HTMLElement | null) => ReactNode;
}

/** What a row shows. `dragName` makes the row draggable onto the board (the DM's creature rows). */
export interface EntryRowProps {
  testid: string;
  name: string;
  /** The family's label; empty draws no badge. */
  family: string;
  book?: string | null | undefined;
  detail?: string | undefined;
  /** Some token it stands for is hidden from the players (the DM's "On this scene"). */
  hidden?: boolean | undefined;
  title: string;
  onOpen: () => void;
  onDragStart?: ((e: React.DragEvent) => void) | undefined;
  actions?: ReactNode;
  /** Its entry's hover card (slice 3). */
  preview?: RowPreview | undefined;
}

const keyboardFocus = (el: Element): boolean => { try { return el.matches(':focus-visible'); } catch { return true; } };

/**
 * A row's hover-card timing (slice 3, made a hook in slice 4 so the Campaign NPCs panel's rows share it): hover intent,
 * keyboard focus, a touch hold, a press elsewhere, blur and Escape. Spread `rowProps` on the row (an `li`, holding the
 * card), `buttonProps` on its main button, and send that button's click through `clicked`, which drops the click of the
 * press that raised the card by holding. Nothing at all when there is no `preview`.
 */
export function useEntryCard(preview: RowPreview | undefined) {
  const li = useRef<HTMLLIElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const intent = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const leave = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);
  const stop = (t: { current: ReturnType<typeof setTimeout> | undefined }) => { clearTimeout(t.current); t.current = undefined; };
  useEffect(() => () => { stop(intent); stop(leave); stop(hold); }, []);
  const set = (o: boolean) => { if (preview && preview.open !== o) preview.onOpenChange(o); };
  const isOpen = !!preview?.open;
  const close = useRef<() => void>(() => undefined);
  close.current = () => set(false);
  // A press anywhere else closes it (a touch screen has no pointer to leave with).
  useEffect(() => {
    if (!isOpen) return undefined;
    const away = (e: PointerEvent) => { if (!li.current?.contains(e.target as Node | null)) close.current(); };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [isOpen]);
  const rowProps = preview ? {
    onPointerEnter: (e: React.PointerEvent) => {
      stop(leave);
      if (e.pointerType === 'touch' || preview.open) return;
      stop(intent);
      intent.current = setTimeout(() => { intent.current = undefined; set(true); }, INTENT_MS);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      stop(intent);
      if (e.pointerType === 'touch') return;
      stop(leave);
      // Focus inside keeps it: the keyboard raised it, or a button on it was pressed.
      leave.current = setTimeout(() => { leave.current = undefined; if (!li.current?.contains(document.activeElement)) set(false); }, LEAVE_MS);
    },
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch' || (e.target as Element).closest('[data-testid="entry-card"]')) return;
      held.current = false;
      stop(hold);
      hold.current = setTimeout(() => { hold.current = undefined; held.current = true; set(true); }, HOLD_MS);
    },
    onPointerUp: () => stop(hold),
    onPointerCancel: () => stop(hold),
    onContextMenu: (e: React.MouseEvent) => { if (held.current || hold.current) e.preventDefault(); },
    onBlur: (e: React.FocusEvent) => { if (!li.current?.contains(e.relatedTarget as Node | null)) { stop(intent); set(false); } },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape' || !preview.open) return;
      e.stopPropagation();
      const inCard = !!(document.activeElement && document.activeElement !== button.current && li.current?.contains(document.activeElement));
      set(false);
      if (inCard) button.current?.focus();
    },
  } : {};
  const buttonProps = preview ? { onFocus: (e: React.FocusEvent) => { if (keyboardFocus(e.currentTarget)) { stop(leave); set(true); } } } : {};
  /** The main button's click: the press that raised the card by holding opens nothing. */
  const clicked = (onOpen: () => void) => {
    if (held.current) { held.current = false; return; }
    onOpen();
  };
  return { li, button, rowProps, buttonProps, clicked };
}

export function EntryRow({ testid, name, family, book, detail, hidden, title, onOpen, onDragStart, actions, preview }: EntryRowProps) {
  const { li, button, rowProps: handlers, buttonProps, clicked } = useEntryCard(preview);
  return (
    // The row itself is what is dragged (a button cannot start a drag in every browser).
    <li ref={li} data-testid={testid} className="flex items-center gap-1" {...(onDragStart ? { draggable: true, onDragStart } : {})} {...handlers}>
      <button ref={button} type="button" className={`flex min-w-0 flex-1 items-baseline gap-1.5 py-1.5 text-left hover:bg-stone-800 ${onDragStart ? 'cursor-grab' : ''}`} title={title}
        onClick={() => clicked(onOpen)} {...buttonProps}>
        <span className="min-w-0 truncate text-stone-100">{name}</span>
        {family && <span className="shrink-0 rounded-full border border-vtt-line px-1.5 text-[0.625rem] leading-4 text-vtt-muted" data-testid="compendium-family">{family}</span>}
        {hidden && <span className="shrink-0 rounded border border-amber-800 px-1 text-[0.625rem] leading-4 text-amber-300" data-testid="compendium-hidden" title="Hidden from the players">hidden</span>}
        {book && <span className="min-w-0 truncate text-xs text-stone-500" data-testid="compendium-book">{book}</span>}
        {detail && <span className="ml-auto shrink-0 pl-1 text-xs text-stone-400" data-testid="compendium-detail">{detail}</span>}
      </button>
      {actions}
      {preview?.open && preview.card(li.current)}
    </li>
  );
}

/** A row with no entry to open: its name and its detail, plain text. */
export function TextRow({ testid, name, detail }: { testid: string; name: string; detail?: string | undefined }) {
  return (
    <li data-testid={testid} className="flex items-baseline gap-1.5 py-1.5">
      <span className="min-w-0 truncate text-stone-200">{name}</span>
      {detail && <span className="ml-auto shrink-0 pl-1 text-xs text-stone-400" data-testid="compendium-detail">{detail}</span>}
    </li>
  );
}

/** How many rows a group shows before "+ N more". */
export const GROUP_SHOWN = 5;

const openKey = (userId: string, key: string) => `tdt-vtt-compendium-group:${userId}:${key}`;
function readOpen(userId: string, key: string): boolean {
  try { return window.localStorage.getItem(openKey(userId, key)) !== 'closed'; } catch { return true; }
}
function writeOpen(userId: string, key: string, open: boolean): void {
  try { if (open) window.localStorage.removeItem(openKey(userId, key)); else window.localStorage.setItem(openKey(userId, key), 'closed'); } catch { /* not remembered */ }
}

/** One group of the default view. `count` rows exist; `row(i)` renders the i-th. Hidden when empty, unless `emptyText`. */
export function CompendiumGroup({ groupKey, title, userId, count, row, emptyText }: {
  groupKey: string; title: string; userId: string; count: number; row: (i: number) => ReactNode; emptyText?: string;
}) {
  const [open, setOpenState] = useState(() => readOpen(userId, groupKey));
  const [all, setAll] = useState(false);
  const body = useId();
  if (count === 0 && !emptyText) return null;
  const setOpen = (o: boolean) => { setOpenState(o); writeOpen(userId, groupKey, o); };
  const shown = all ? count : Math.min(count, GROUP_SHOWN);
  return (
    <section data-testid={`compendium-group-${groupKey}`} data-open={open ? 'true' : 'false'} className="mb-2">
      <button type="button" className="flex w-full items-center gap-2 py-1 text-left font-display text-xs font-semibold tracking-wide text-vtt-gold vtt-title"
        aria-expanded={open} aria-controls={body} data-testid={`compendium-group-head-${groupKey}`} onClick={() => setOpen(!open)}>
        <span aria-hidden="true" className="w-2.5 shrink-0 text-[0.625rem]">{open ? '▾' : '▸'}</span>
        {title}
        {count > 0 && <span className="ml-auto font-sans text-[0.6875rem] font-normal text-vtt-muted">{count}</span>}
      </button>
      <div id={body} hidden={!open}>
        {count === 0 ? <p className="py-1 text-xs text-stone-400" data-testid={`compendium-group-empty-${groupKey}`}>{emptyText}</p> : (
          <ul className="divide-y divide-stone-800">{Array.from({ length: shown }, (_, i) => row(i))}</ul>
        )}
        {count > shown && (
          <button type="button" className="mt-0.5 text-xs text-vtt-muted underline hover:text-vtt-fg" data-testid={`compendium-group-more-${groupKey}`} onClick={() => setAll(true)}>
            + {count - shown} more
          </button>
        )}
      </div>
    </section>
  );
}
